import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../lib/brand";

const NAV = [
  { to: "/program", label: "The Back Office Blueprint" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-navy/90 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="w-7 h-7 bg-safety flex flex-col justify-center items-center gap-[3px] group-hover:rotate-90 transition-transform duration-300" aria-hidden="true">
            <span className="block w-3.5 h-[2px] bg-navy" />
            <span className="block w-2.5 h-[2px] bg-navy" />
            <span className="block w-1.5 h-[2px] bg-navy" />
          </span>
          <span className="font-display font-extrabold tracking-tight text-white text-lg">
            {brand.business_name.split(" ")[0]}<span className="text-safety"> {brand.business_name.split(" ").slice(1).join(" ")}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-safety" : "text-slate-300 hover:text-white"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <button
                data-testid="nav-dashboard-btn"
                onClick={() => navigate(user.role === "admin" ? "/admin" : "/hub")}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                {user.role === "admin" ? "Admin" : "My Hub"}
              </button>
              <button
                data-testid="nav-logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="text-sm font-medium text-slate-500 hover:text-white transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Member Login
            </Link>
          )}
          <Link
            to="/apply"
            data-testid="nav-apply-btn"
            className="bg-safety hover:bg-safety-hover text-white text-sm font-semibold px-5 py-2.5 flex items-center gap-2 transition-colors"
          >
            Apply — Founding Cohort <ArrowRight size={15} />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-navy border-t border-white/10 px-5 py-6 flex flex-col gap-4" data-testid="nav-mobile">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className="text-slate-200 font-medium text-base"
            >
              {n.label}
            </NavLink>
          ))}
          <Link to="/login" onClick={() => setOpen(false)} className="text-slate-400 text-base">Member Login</Link>
          <Link
            to="/apply"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-apply-btn"
            className="bg-safety text-white font-semibold px-5 py-3 text-center"
          >
            Apply — Founding Cohort
          </Link>
        </div>
      )}
    </header>
  );
}
